import React from 'react';
import { ActionDropdown } from 'components/common';
import PropTypes from 'prop-types';
import {
  MdOpenInNew, MdExpandMore, MdExpandLess, MdApartment,
  MdMoreVert, MdSwapHoriz, MdEdit, MdPerson,
} from 'react-icons/md';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';
import PersonHoverCard from 'components/common/PersonHoverCard';
import { NODE_WIDTH, NODE_HEIGHT, employeeDetailPath } from './orgChartHelpers';

/**
 * One card on the canvas.
 *
 * Deliberately shows only avatar, code and full name. Title, department, site and
 * email live in the hover card and the side panel — cramming them onto every card
 * turns a 670-node chart into a wall of text and hides the shape of the org,
 * which is the one thing this view exists for.
 *
 * The ⋮ menu is portalled to the body by ActionDropdown and positioned from
 * getBoundingClientRect, so it lands correctly and at normal size even though the
 * card itself sits inside a scaled canvas.
 */
const OrgChartNode = ({
  node, isFocused, isDropTarget, isDragging, onToggle, onFocus, onChangeManager,
  onEdit, wasDragged, dragHandlers,
}) => {
  const { isCompanyRoot, isMatch, isContext, collapsed, childCount, totalCount } = node;
  // active is only false when the status filter has pulled leavers in, so it is
  // worth calling out rather than blending into the tree.
  const isInactive = node.active === false;

  if (isCompanyRoot) {
    return (
      <div
        style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-bg-slate-800 tw-text-white tw-shadow-md tw-px-3"
      >
        <MdApartment size={20} className="tw-opacity-80" />
        <span className="tw-text-sm tw-font-semibold tw-truncate tw-max-w-full">{node.name}</span>
        <span className="tw-text-[11px] tw-text-slate-300">{totalCount} reporting lines</span>
      </div>
    );
  }

  // Mutually exclusive states, resolved here rather than by stacking conflicting
  // utility classes whose winner would depend on CSS source order.
  let frameClass = 'tw-border-slate-200 tw-bg-white';
  if (isInactive) frameClass = 'tw-border-slate-200 tw-bg-slate-50 tw-border-dashed';
  if (isMatch) frameClass = 'tw-border-amber-400 tw-bg-amber-50 tw-ring-2 tw-ring-amber-200';
  else if (isContext) frameClass = 'tw-border-slate-100 tw-bg-white tw-opacity-45';
  if (isFocused) frameClass = 'tw-border-primary-500 tw-bg-primary-50 tw-ring-2 tw-ring-primary-200';
  if (isDropTarget) frameClass = 'tw-border-emerald-500 tw-bg-emerald-50 tw-ring-2 tw-ring-emerald-300';

  return (
    <div
      style={{ width: NODE_WIDTH, height: NODE_HEIGHT, opacity: isDragging ? 0.4 : 1 }}
      className="tw-relative"
      {...dragHandlers}
    >
      <PersonHoverCard person={node} className="tw-w-full tw-h-full">
        <a
          href={employeeDetailPath(node.id)}
          target="_blank"
          rel="noopener noreferrer"
          draggable={false}
          onClick={(event) => {
            // Ending a pan on top of a card must not open that card.
            if (wasDragged()) {
              event.preventDefault();
              return;
            }
            // The ↗ and ⋮ affordances own their clicks; letting the card's own
            // handler swallow them made them look clickable and do nothing.
            if (event.target.closest('[data-orgchart-open]')) return;
            if (event.target.closest('[data-orgchart-nopan]')) {
              event.preventDefault();
              return;
            }
            // Plain click selects; ctrl/cmd-click keeps the browser's own
            // open-in-new-tab behaviour.
            if (event.ctrlKey || event.metaKey || event.shiftKey) return;
            event.preventDefault();
            onFocus(node);
          }}
          // focus:outline-none — Tailwind preflight is disabled in this app, so a
          // clicked card would otherwise keep the browser's default focus box.
          // The ring below replaces it, only for keyboard focus.
          className={`tw-flex tw-w-full tw-h-full tw-items-center tw-gap-2.5 tw-pl-3 tw-pr-1 tw-rounded-lg tw-border tw-border-solid tw-shadow-sm tw-transition-all hover:tw-shadow-md tw-no-underline tw-outline-none focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 ${frameClass}`}
        >
          <EmployeeAvatar name={node.name} size={38} fontSize={13} />

          <span className="tw-min-w-0 tw-flex-1 tw-text-left">
            <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate">
              {node.full_name || node.name}
            </span>
            <span className="tw-block tw-text-[11px] tw-text-slate-400 tw-truncate">
              {node.name}
              {isInactive && (
                <span className="tw-ml-1.5 tw-inline-flex tw-items-center tw-px-1.5 tw-rounded-xl tw-bg-red-50 tw-text-red-600 tw-text-[10px] tw-font-bold">
                  inactive
                </span>
              )}
            </span>
          </span>

          <span className="tw-flex tw-items-center tw-shrink-0">
            <span
              data-orgchart-open
              data-orgchart-nopan
              title={`Open ${node.full_name || node.name}'s employee record in a new tab`}
              className="tw-inline-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded-md tw-text-slate-300 hover:tw-bg-slate-100 hover:tw-text-primary-600 tw-transition-colors"
            >
              <MdOpenInNew size={13} />
            </span>

            <span data-orgchart-nopan className="tw-inline-flex">
              <ActionDropdown
                align="end"
                menuWidth={190}
                unstyledTrigger
                triggerClassName="tw-inline-flex tw-items-center tw-justify-center tw-h-6 tw-w-5 tw-rounded-md tw-border-0 tw-bg-transparent tw-text-slate-300 hover:tw-bg-slate-100 hover:tw-text-primary-600 tw-transition-colors"
                trigger={<MdMoreVert size={15} />}
                items={[
                  { label: 'Change manager', icon: <MdSwapHoriz size={14} />, onClick: () => onChangeManager(node) },
                  { label: 'Edit employee', icon: <MdEdit size={13} />, onClick: () => onEdit(node) },
                  { label: 'Open record', icon: <MdPerson size={13} />, onClick: () => window.open(employeeDetailPath(node.id), '_blank', 'noopener') },
                ]}
              />
            </span>
          </span>
        </a>
      </PersonHoverCard>

      {totalCount > 0 && (
        <button
          type="button"
          data-orgchart-nopan
          onClick={() => onToggle(node.id)}
          title={childCount < totalCount
            ? `${totalCount} direct reports · ${childCount} visible with current filters`
            : (collapsed ? `Show ${totalCount} direct reports` : `Hide ${totalCount} direct reports`)}
          className="tw-absolute tw-left-1/2 -tw-translate-x-1/2 -tw-bottom-3 tw-inline-flex tw-items-center tw-gap-0.5 tw-px-2 tw-h-6 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-bold tw-text-slate-600 tw-shadow-sm hover:tw-border-primary-300 hover:tw-text-primary-600 tw-transition-colors"
        >
          {collapsed ? <MdExpandMore size={13} /> : <MdExpandLess size={13} />}
          {totalCount}
        </button>
      )}
    </div>
  );
};

OrgChartNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    full_name: PropTypes.string,
    isCompanyRoot: PropTypes.bool,
    isMatch: PropTypes.bool,
    isContext: PropTypes.bool,
    collapsed: PropTypes.bool,
    childCount: PropTypes.number,
    totalCount: PropTypes.number,
    active: PropTypes.bool,
  }).isRequired,
  isFocused: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  isDragging: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onChangeManager: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  wasDragged: PropTypes.func.isRequired,
  dragHandlers: PropTypes.object,
};

export default React.memo(OrgChartNode);
